import type { ExtensionPresence, SessionState, SyncCommandInput } from './client';
import { startLeaderTransport } from './leaderTransport';
import { startSharedWorkerTransport } from './sharedWorkerTransport';

// One realtime transport interface, two implementations (see refs/realtime-sync
// "Connection management"):
//
//   1. SharedWorker broker (sharedWorkerTransport) — preferred. One worker owns
//      the single RTDB socket and outlives any individual slide iframe, so Gamma
//      scrolling that destroys/recreates slides doesn't churn the connection.
//   2. Web-Locks leader (leaderTransport) — fallback when SharedWorker is
//      unavailable (e.g. sandboxed opaque-origin iframes). One leader frame holds
//      the socket; leadership (and the socket) moves when that frame is torn down.

export interface TransportHandlers {
  onPresence: (presence: ExtensionPresence) => void;
  onSessions: (sessions: Record<string, SessionState>) => void;
}

export interface RealtimeTransport {
  // Push a start/stop command (idempotent fire-and-forget).
  sendCommand: (input: SyncCommandInput) => Promise<void>;
  // Identify whose app presence to publish (null clears it).
  setPresenceIdentity: (userId: string | null) => void;
  // Tear everything down.
  stop: () => void;
}

// `ownerKey` is the teams store's owner id for the active team (null until teams
// data resolves). The transports announce it so a team switch invalidates the
// previous owner's sign-in (SharedWorker re-auth / client cache reset) instead of
// silently keeping every read and write under the old `/sync/{ownerId}`.
export function startTransport(
  handlers: TransportHandlers,
  ownerKey: string | null = null
): RealtimeTransport {
  if (typeof window !== 'undefined' && typeof SharedWorker !== 'undefined') {
    try {
      return startSharedWorkerTransport(handlers, ownerKey);
    } catch {
      // SharedWorker construction throws synchronously in sandboxed opaque-origin
      // iframes — fall back to the in-frame leader transport.
    }
  }
  return startLeaderTransport(handlers, ownerKey);
}
